import React, {useState, useEffect, useMemo} from 'react';
import {
    Search,
    X,
    Loader,
    Filter,
    Edit2,
    Trash2,
    Package,
    ChevronDown,
    ShoppingCart,
    Archive,
    Copy,
    Eye,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Barcode,
    Printer,
    AlertTriangle,
    FileText,
    ChevronLeft,
    ChevronRight,
    RotateCcw,
    Check
} from 'lucide-react';
import {useQuery, useQueryClient, useMutation} from '@tanstack/react-query';
import secureStorage from '../../../utils/secureStorage';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {api} from '../../../config/api';
import ProductDetailsModal from './ProductDetailsModal';
import LabelPrintModal from '../../Labels/LabelPrintModal';
import AppleSelect from '../../Common/AppleSelect';
import Button, {buttonClass} from '../../Common/Button';
import ActionMenu from '../../Common/ActionMenu';
import ActiveFilters from '../../Common/ActiveFilters';
import Notification from "../../Common/Notification";
import {Modal, ModalFooter, mobileListClass, mobileRowClass} from '../../../stock/StockUI';
import {SwitchRow} from '../../Setting/SettingsUI';

const YES_NO_OPTIONS = [{label: 'Tous', value: ''}, {label: 'Oui', value: 'true'}, {label: 'Non', value: 'false'}];
const VARIANTS_OPTIONS = [{label: 'Tous', value: ''}, {label: 'Avec', value: 'true'}, {label: 'Sans', value: 'false'}];
const STATUS_OPTIONS = [{label: 'Tous', value: ''}, {label: 'Actif', value: 'true'}, {label: 'Inactif', value: 'false'}];

// Label above a filter control, as in the other catalogue popups
const FilterField = ({label, children}) => (
    <div>
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">{label} :</span>
        {children}
    </div>
);

// iOS-style segmented control (Tous / Oui / Non)
const SegmentedControl = ({value, onChange, options}) => (
    <div role="radiogroup" className="grid grid-flow-col auto-cols-fr p-0.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
        {options.map(o => (
            <button
                key={o.label}
                type="button"
                role="radio"
                aria-checked={value === o.value}
                onClick={() => onChange(o.value)}
                className={`py-1.5 text-sm font-medium rounded-md transition-all ${
                    value === o.value
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

// 🎯 Composant Header Triable
const SortableHeader = ({field, currentField, direction, onSort, children}) => {
    const isActive = currentField === field;

    return (
        <th
            className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => onSort(field)}
        >
            <div className="flex items-center gap-2">
                <span>{children}</span>
                {isActive ? (
                    direction === 'asc' ? (
                        <ArrowUp className="w-4 h-4 text-primary"/>
                    ) : (
                        <ArrowDown className="w-4 h-4 text-primary"/>
                    )
                ) : (
                    <ArrowUpDown
                        className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"/>
                )}
            </div>
        </th>
    );
};

// Row actions: one "⋯" menu for desktop rows and mobile cards
const ProductActions = ({product, onEdit, onArchive, onDuplicate, onViewDetails, onPrintLabel, onProductReport}) => (
    <ActionMenu
        label={`Actions pour ${product.name}`}
        items={[
            {label: 'Modifier', icon: Edit2, onClick: () => onEdit(product.id)},
            {label: 'Détails', icon: Eye, onClick: () => onViewDetails(product.id)},
            {label: 'Dupliquer', icon: Copy, onClick: () => onDuplicate(product.id)},
            {divider: true},
            {label: 'Imprimer étiquette', icon: Printer, onClick: () => onPrintLabel(product)},
            {label: 'Fiche produit (PDF)', icon: FileText, onClick: () => onProductReport(product)},
            {divider: true},
            {label: 'Archiver', icon: Archive, danger: true, onClick: () => onArchive(product.id)},
        ]}
    />
);

const ProductList = ({stockableFilter, ingredientFilter}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    // ── URL-persisted state ──────────────────────────────────────────
    const currentPage   = Math.max(1, parseInt(searchParams.get('page')    || '1',  10));
    const itemsPerPage  = (() => { const v = parseInt(searchParams.get('perPage') || '10', 10); return [10,25,50,100].includes(v) ? v : 10; })();
    const debouncedSearchTerm        = searchParams.get('q')       || '';
    const debouncedBarcodeSearchTerm = searchParams.get('barcode') || '';
    const filterCategory   = searchParams.get('category')  || '';
    const filterBrand      = searchParams.get('brand')     || '';
    const filterVendable   = searchParams.get('vendable')  || '';
    const filterStockable  = stockableFilter || searchParams.get('stockable') || '';
    const filterIngredient = ingredientFilter || searchParams.get('ingredient') || '';
    const filterActive     = searchParams.get('active')    || '';
    const filterStockAlert = searchParams.get('stockAlert') || '';
    const filterVariants   = searchParams.get('variants')  || '';
    // Stable object reference — only changes when actual values change (prevents queryKey churn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const filters = useMemo(() => ({
        category:     filterCategory,
        brand:        filterBrand,
        isVendable:   filterVendable,
        isStockable:  filterStockable,
        isIngredient: filterIngredient,
        active:       filterActive,
        stockAlert:   filterStockAlert,
        hasVariants:  filterVariants,
    }), [filterCategory, filterBrand, filterVendable, filterStockable, filterIngredient, filterActive, filterStockAlert, filterVariants]);

    /** Low-level helper – merge key/value into current params.
     *  push=true  → adds a history entry (use for page navigation)
     *  push=false → replaces current entry (use for search/filter: no back-button spam)
     */
    const updateParams = (updates, push = false) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            Object.entries(updates).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') next.set(k, String(v));
                else next.delete(k);
            });
            return next;
        }, { replace: !push });
    };

    // Page navigation → push history entry so Back button works
    const setCurrentPage  = (p) => updateParams({ page: p > 1 ? p : null }, true);
    // Changing items-per-page resets to page 1, replace (no extra history entry)
    const setItemsPerPage = (n) => updateParams({ perPage: n !== 10 ? n : null, page: null }, false);

    // ── Local input state (debounced → URL) ─────────────────────────
    const [searchTerm,        setSearchTerm]        = useState(() => searchParams.get('q')       || '');
    const [barcodeSearchTerm, setBarcodeSearchTerm] = useState(() => searchParams.get('barcode') || '');

    // Keep inputs in sync when URL changes externally (e.g. browser Back button)
    const urlQ       = searchParams.get('q')       || '';
    const urlBarcode = searchParams.get('barcode') || '';
    useEffect(() => { setSearchTerm(urlQ);       }, [urlQ]);       // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { setBarcodeSearchTerm(urlBarcode); }, [urlBarcode]); // eslint-disable-line react-hooks/exhaustive-deps

    // Temp filters used only inside the modal
    const [tempFilters, setTempFilters] = useState({ category: '', brand: '', isVendable: '', isStockable: '', active: '', stockAlert: '', hasVariants: '' });
    const [showFiltersModal, setShowFiltersModal] = useState(false);

    // When stockableFilter prop is provided, hide the stockable filter from the UI
    const hasStockableProp = !!stockableFilter;
    // When ingredientFilter prop is provided (Catalogue → Ingrédients tab), show purchase price instead of the vendable/stockable columns
    const hasIngredientProp = !!ingredientFilter;

    // Selection
    const [selectedAll,   setSelectedAll]   = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);

    // Sorting
    const [sortField,     setSortField]     = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');

    // Bulk actions
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingAction,     setPendingAction]     = useState(null);

    // Product details modal
    const [detailsModalOpen,  setDetailsModalOpen]  = useState(false);
    const [detailsProductId,  setDetailsProductId]  = useState(null);

    // Label print modal
    const [labelItems, setLabelItems] = useState([]);

    // Notification
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });

    // Pagination (totalPages comes from API response)
    const [totalPages, setTotalPages] = useState(1);

    // Refs

    // Animations injection
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
                                        @keyframes slideDown {from {opacity:0; transform:translateY(-20px);} to {opacity:1; transform:translateY(0);} }
                                        @keyframes fadeIn {from {opacity:0;} to {opacity:1;} }
                                        @keyframes scaleIn {from {opacity:0; transform:scale(.95);} to {opacity:1; transform:scale(1);} }
                                        .selection-bar-appear {animation: slideDown .3s ease-out forwards;}
                                        .modal-appear {animation: fadeIn .2s ease-out forwards;}
                                        .card-appear {animation: scaleIn .2s ease-out forwards;}
                                        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);


    // Debounce name search → URL
    // Guarded: only push when the trimmed value actually differs from the URL,
    // otherwise every debounce tick (incl. the one right after urlQ → searchTerm
    // sync on mount) creates a redundant setSearchParams navigation, which can
    // ping-pong with the urlQ→setSearchTerm effect above.
    useEffect(() => {
        const t = setTimeout(() => {
            const trimmed = searchTerm.trim();
            if (trimmed !== urlQ) {
                updateParams({ q: trimmed || null, page: null });
            }
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // Debounce barcode search → URL (same guard as above)
    useEffect(() => {
        const t = setTimeout(() => {
            const trimmed = barcodeSearchTerm.trim();
            if (trimmed !== urlBarcode) {
                updateParams({ barcode: trimmed || null, page: null });
            }
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [barcodeSearchTerm]);


    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        if (type === 'success') setTimeout(() => setNotification(p => ({...p, show: false})), 4000);
    };
    const hideNotification = () => setNotification(p => ({...p, show: false}));

    // Fetch categories
    const fetchCategories = async () => {
        try {
            const accountId = await secureStorage.getItem('selectedAccount');
            const res = await api.get('/categories', {headers: {'X-account-id': accountId.id}, params: {itemsPerPage: 100}});
            return res.data.data || res.data.items || res.data || [];
        } catch {
            return [];
        }
    };
    const {data: categories = []} = useQuery({queryKey: ['categories'], queryFn: fetchCategories, staleTime: 300000});

    // Fetch brands
    const fetchBrands = async () => {
        try {
            const accountId = await secureStorage.getItem('selectedAccount');
            const res = await api.get('/brands', {headers: {'X-account-id': accountId.id}, params: {itemsPerPage: 100}});
            return res.data.data || res.data.items || res.data || [];
        } catch {
            return [];
        }
    };
    const {data: brands = []} = useQuery({queryKey: ['brands'], queryFn: fetchBrands, staleTime: 300000});

    // Fetch products — pure queryFn, no setState inside (React Query v5 anti-pattern)
    const fetchProducts = async () => {
        const accountId = await secureStorage.getItem('selectedAccount');
        // When barcode search is active, fetch all products for client-side filtering
        const perPage = debouncedBarcodeSearchTerm ? 1000 : itemsPerPage;
        const page = debouncedBarcodeSearchTerm ? 1 : currentPage;
        // NB: the API's collection provider reads `itemsPerPage` (API Platform convention),
        // not `per_page` — using the wrong name silently falls back to its default of 10.
        let url = `/products?page=${page}&itemsPerPage=${perPage}`;
        if (debouncedSearchTerm.trim()) url += `&name=${encodeURIComponent(debouncedSearchTerm.trim())}`;
        if (filters.category) url += `&category=${encodeURIComponent(filters.category)}`;
        if (filters.brand) url += `&brand=${encodeURIComponent(filters.brand)}`;
        if (filters.isVendable !== '') url += `&isVendable=${filters.isVendable}`;
        if (filters.isStockable !== '') url += `&isStockable=${filters.isStockable}`;
        if (filters.isIngredient !== '') url += `&isIngredient=${filters.isIngredient}`;
        if (filters.active !== '') url += `&active=${filters.active}`;
        if (filters.stockAlert === 'true') url += `&stock_alert=true`;
        if (filters.hasVariants !== '') url += `&has_variants=${filters.hasVariants}`;

        const res = await api.get(url, {headers: {'X-account-id': accountId.id}});
        // Return the full response so pagination can be derived outside queryFn
        return res.data;
    };
    const {data: productsResponse, isLoading} = useQuery({
        queryKey: ['products', currentPage, debouncedSearchTerm, filters, itemsPerPage, debouncedBarcodeSearchTerm],
        queryFn: fetchProducts,
        placeholderData: (prev) => prev, // React Query v5: replaces deprecated keepPreviousData
    });

    // Derive products array from full API response
    const products = useMemo(() => {
        if (!productsResponse) return [];
        if (Array.isArray(productsResponse)) return productsResponse;
        return productsResponse.data || productsResponse.items || [];
    }, [productsResponse]);

    // Sync totalPages OUTSIDE queryFn (calling setState inside queryFn causes infinite loops)
    useEffect(() => {
        if (!productsResponse) return;
        if (productsResponse.pagination) {
            setTotalPages(debouncedBarcodeSearchTerm ? 1 : (productsResponse.pagination.totalPages || 1));
        } else if (productsResponse.meta) {
            setTotalPages(debouncedBarcodeSearchTerm ? 1 : Math.ceil(productsResponse.meta.total / productsResponse.meta.per_page));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productsResponse]);

    // Sort products
    const sortedProducts = React.useMemo(() => {
        if (!products || products.length === 0) return [];

        let list = [...products];

        // Client-side barcode filter
        if (debouncedBarcodeSearchTerm) {
            const term = debouncedBarcodeSearchTerm.toLowerCase().trim();
            list = list.filter(p =>
                p.barcode?.toLowerCase().includes(term) ||
                p.variants?.some(v => v.barcode?.toLowerCase().includes(term))
            );
        }

        return list.sort((a, b) => {
            let aValue, bValue;

            switch (sortField) {
                case 'name':
                    aValue = a.name?.toLowerCase() || '';
                    bValue = b.name?.toLowerCase() || '';
                    break;
                case 'category':
                    aValue = a.category?.name?.toLowerCase() || '';
                    bValue = b.category?.name?.toLowerCase() || '';
                    break;
                case 'price':
                    // Handle products with variants by using the minimum variant price
                    if (a.variants && a.variants.length > 0) {
                        const aPrices = a.variants
                            .map(v => parseFloat(v.price))
                            .filter(p => !isNaN(p) && p > 0);
                        aValue = aPrices.length > 0 ? Math.min(...aPrices) : 0;
                    } else {
                        aValue = parseFloat(a.price) || 0;
                    }

                    if (b.variants && b.variants.length > 0) {
                        const bPrices = b.variants
                            .map(v => parseFloat(v.price))
                            .filter(p => !isNaN(p) && p > 0);
                        bValue = bPrices.length > 0 ? Math.min(...bPrices) : 0;
                    } else {
                        bValue = parseFloat(b.price) || 0;
                    }
                    break;
                case 'active':
                    aValue = a.active ? 1 : 0;
                    bValue = b.active ? 1 : 0;
                    break;
                case 'isVendable':
                    aValue = a.isVendable ? 1 : 0;
                    bValue = b.isVendable ? 1 : 0;
                    break;
                case 'isStockable':
                    aValue = a.isStockable ? 1 : 0;
                    bValue = b.isStockable ? 1 : 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
    }, [products, sortField, sortDirection, debouncedBarcodeSearchTerm]);

    // Keep selection consistent when data changes
    useEffect(() => {
        if (selectedAll) {
            setSelectedItems(sortedProducts.map(p => p.id));
        } else {
            setSelectedItems(prev => prev.filter(id => sortedProducts.some(p => p.id === id)));
        }
    }, [sortedProducts, selectedAll]);

    // Mutations
    const updateProductsMutation = useMutation({
        mutationFn: async ({productIds, updateData}) => {
            const accountId = await secureStorage.getItem('selectedAccount');
            const toUpdate = products.filter(p => productIds.includes(p.id));
            return Promise.all(
                toUpdate.map(p =>
                    api.put(
                        `/products/${p.id}`,
                        {
                            id: p.id,
                            name: p.name,
                            description: p.description || '',
                            active: p.active,
                            isVendable: p.isVendable,
                            isStockable: p.isStockable,
                            ...updateData
                        },
                        {
                            headers: {
                                'X-account-id': accountId.id,
                                'Accept': 'application/json',
                                'Content-Type': 'application/json'
                            }
                        }
                    )
                )
            );
        },
        onMutate: () => showNotification('loading', 'Mise à jour en cours...'),
        onSuccess: () => {
            queryClient.invalidateQueries(['products']);
            setSelectedItems([]);
            setSelectedAll(false);
            setShowConfirmDialog(false);
            setPendingAction(null);
            showNotification('success', 'Produits mis à jour');
        },
        onError: (e) => {
            showNotification('error', `Erreur: ${e.message}`);
            setShowConfirmDialog(false);
            setPendingAction(null);
        }
    });

    const deleteProductsMutation = useMutation({
        mutationFn: async (productIds) => {
            const accountId = await secureStorage.getItem('selectedAccount');
            return Promise.all(
                productIds.map(id => api.delete(`/products/${id}`, {headers: {'X-account-id': accountId.id}}))
            );
        },
        onMutate: () => showNotification('loading', 'Suppression en cours...'),
        onSuccess: () => {
            queryClient.invalidateQueries(['products']);
            setSelectedItems([]);
            setSelectedAll(false);
            setShowConfirmDialog(false);
            setPendingAction(null);
            showNotification('success', 'Produits supprimés');
        },
        onError: (e) => {
            showNotification('error', `Erreur: ${e.message}`);
            setShowConfirmDialog(false);
            setPendingAction(null);
        }
    });

    // Selection handlers
    const handleSelectAll = () => {
        const next = !selectedAll;
        setSelectedAll(next);
        setSelectedItems(next ? sortedProducts.map(p => p.id) : []);
    };
    const handleSelectItem = (id, e) => {
        e.stopPropagation();
        setSelectedItems(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]));
    };

    // Sorting handler
    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Navigation
    // returnTo: the product page's "Retour" comes back to this list, with its filters and page
    const handleEditProduct = (id) => navigate(`/catalogue/product/edit/${id}`, {state: {returnTo: location.pathname + location.search}});


    // 🎯 Handler pour archiver un produit
    const handleArchiveProduct = async (productId) => {
        setPendingAction({type: 'archive', ids: [productId]});
        setShowConfirmDialog(true);
    };

    // 🎯 Handler pour dupliquer un produit
    const handleDuplicateProduct = async (productId) => {
        try {
            showNotification('loading', 'Duplication en cours...');
            const product = sortedProducts.find(p => p.id === productId);
            if (!product) return;

            const accountId = await secureStorage.getItem('selectedAccount');
            const duplicatedProduct = {
                ...product,
                name: `${product.name} (Copie)`,
                barcode: product.barcode ? `${product.barcode}-COPY` : '',
            };
            delete duplicatedProduct.id;
            delete duplicatedProduct.createdAt;
            delete duplicatedProduct.updatedAt;

            await api.post('/products', duplicatedProduct, {
                headers: {'X-account-id': accountId.id}
            });

            queryClient.invalidateQueries(['products']);
            showNotification('success', 'Produit dupliqué avec succès !');
        } catch (error) {
            console.error('Erreur duplication:', error);
            showNotification('error', 'Erreur lors de la duplication');
        }
    };

    // 🎯 Handler pour voir les détails
    const handleViewDetails = (productId) => {
        setDetailsProductId(productId);
        setDetailsModalOpen(true);
    };

    // 🎯 Handler impression étiquette(s)
    const handlePrintLabel = (product) => setLabelItems([product]);
    const handlePrintLabelsBulk = () => {
        const items = sortedProducts.filter(p => selectedItems.includes(p.id));
        if (items.length) setLabelItems(items);
    };

    // 🎯 Handler génération fiche produit (PDF)
    const handleProductReport = async (product) => {
        try {
            showNotification('loading', 'Génération de la fiche produit...');
            const accountId = await secureStorage.getItem('selectedAccount');
            const res = await api.get(`/product/${product.id}/report`, {
                headers: {'X-account-id': accountId.id}
            });
            window.open(res.data.url, '_blank', 'noopener,noreferrer');
            showNotification('success', 'Fiche produit générée avec succès !');
        } catch (error) {
            console.error('Erreur génération fiche produit:', error);
            showNotification('error', error.response?.data?.error || 'Erreur lors de la génération de la fiche produit');
        }
    };


    // Bulk actions
    const handleBulkAction = (type, value = null) => {
        if (!selectedItems.length) return;
        setPendingAction({type, value});
        setShowConfirmDialog(true);
    };
    const confirmAction = () => {
        if (!pendingAction) return;
        if (pendingAction.type === 'delete') {
            deleteProductsMutation.mutate(selectedItems);
        } else if (pendingAction.type === 'vendable') {
            updateProductsMutation.mutate({productIds: selectedItems, updateData: {isVendable: pendingAction.value}});
        } else if (pendingAction.type === 'stockable') {
            updateProductsMutation.mutate({productIds: selectedItems, updateData: {isStockable: pendingAction.value}});
        }
    };
    const getConfirmationMessage = () => {
        if (!pendingAction) return '';
        const count = selectedItems.length;
        const txt = `${count} produit${count > 1 ? 's' : ''}`;
        switch (pendingAction.type) {
            case 'delete':
                return `Supprimer ${txt} ? Action irréversible.`;
            case 'vendable':
                return pendingAction.value ? `Marquer ${txt} vendable ?` : `Marquer ${txt} non vendable ?`;
            case 'stockable':
                return pendingAction.value ? `Marquer ${txt} stockable ?` : `Marquer ${txt} non stockable ?`;
            default:
                return '';
        }
    };

    // Filter modal helpers
    const openFiltersModal = () => {
        setTempFilters({...filters});
        setShowFiltersModal(true);
    };
    const applyFilters = () => {
        updateParams({
            category:   tempFilters.category   || null,
            brand:      tempFilters.brand      || null,
            vendable:   tempFilters.isVendable  || null,
            stockable:  tempFilters.isStockable || null,
            active:     tempFilters.active      || null,
            stockAlert: tempFilters.stockAlert  || null,
            variants:   tempFilters.hasVariants || null,
            page: null,
        });
        setShowFiltersModal(false);
    };
    const clearAllFilters = () => {
        setSearchTerm('');
        setBarcodeSearchTerm('');
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            ['q','barcode','category','brand','vendable','stockable','active','stockAlert','variants','page','perPage'].forEach(k => next.delete(k));
            return next;
        }, { replace: true });
        setShowFiltersModal(false);
    };
    const removeFilter = (filterKey) => {
        const paramKey = {isVendable: 'vendable', isStockable: 'stockable', hasVariants: 'variants'}[filterKey] || filterKey;
        updateParams({ [paramKey]: null, page: null });
    };
    const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

    // Pagination
    const pages = Array.from({length: totalPages}, (_, i) => i + 1);
    const handlePrevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
    const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);

    if (isLoading) {
        return (
            <div className="flex flex-col justify-center items-center h-64 space-y-4">
                <Loader className="w-10 h-10 text-primary animate-spin"/>
                <p className="text-gray-500 dark:text-gray-400">Chargement des produits...</p>
            </div>
        );
    }

    return (
        <div className="max-w-full">
            {/* Search + Filter */}
            <div className="flex flex-col sm:flex-row">
                <div className="relative flex-1">
                    <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                    <input
                        type="text"
                        placeholder="Rechercher un produit..."
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-primary transition-all placeholder:text-gray-400 shadow-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="relative flex-1 sm:max-w-xs">
                    <Barcode className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
                    <input
                        type="text"
                        placeholder="Code barre..."
                        className="w-full pl-10 pr-8 py-3 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-all placeholder:text-gray-400 shadow-sm font-mono"
                        value={barcodeSearchTerm}
                        onChange={(e) => setBarcodeSearchTerm(e.target.value)}
                    />
                    {barcodeSearchTerm && (
                        <button
                            onClick={() => {
                                setBarcodeSearchTerm('');
                                updateParams({ barcode: null, page: null });
                            }}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <X className="w-4 h-4"/>
                        </button>
                    )}
                </div>
                <button
                    onClick={openFiltersModal}
                    className="relative flex items-center justify-center sm:justify-start gap-2 px-6 py-3
                            bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
                            hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm hover:shadow transition-all"
                >
                    <Filter className="w-5 h-5 text-gray-600 dark:text-gray-300"/>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Filtres</span>
                    {activeFiltersCount > 0 && (
                        <span
                            className="absolute -top-2 -right-2 bg-primary text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg">
                            {activeFiltersCount}
                        </span>
                    )}
                </button>
            </div>

            {/* Active Filters */}
            <ActiveFilters
                onClearAll={clearAllFilters}
                chips={[
                    debouncedSearchTerm && {key: 'search', label: `Recherche: ${debouncedSearchTerm}`, onRemove: () => setSearchTerm('')},
                    filters.category && {
                        key: 'category',
                        label: categories.find(c => c.id === filters.category)?.name || '…',
                        color: categories.find(c => c.id === filters.category)?.color,
                        onRemove: () => removeFilter('category'),
                    },
                    filters.brand && {key: 'brand', label: `Marque: ${brands.find(b => b.id === filters.brand)?.name || '…'}`, onRemove: () => removeFilter('brand')},
                    filters.isVendable !== '' && {key: 'isVendable', label: `Vendable: ${filters.isVendable === 'true' ? 'Oui' : 'Non'}`, onRemove: () => removeFilter('isVendable')},
                    filters.isStockable !== '' && {key: 'isStockable', label: `Stockable: ${filters.isStockable === 'true' ? 'Oui' : 'Non'}`, onRemove: () => removeFilter('isStockable')},
                    filters.active !== '' && {key: 'active', label: `Statut: ${filters.active === 'true' ? 'Actif' : 'Inactif'}`, onRemove: () => removeFilter('active')},
                    filters.hasVariants !== '' && {key: 'hasVariants', label: `Déclinaisons: ${filters.hasVariants === 'true' ? 'Avec' : 'Sans'}`, onRemove: () => removeFilter('hasVariants')},
                    filters.stockAlert === 'true' && {key: 'stockAlert', label: 'Alerte de stock', icon: AlertTriangle, onRemove: () => removeFilter('stockAlert')},
                    debouncedBarcodeSearchTerm && {
                        key: 'barcode', label: debouncedBarcodeSearchTerm, icon: Barcode, mono: true,
                        onRemove: () => {
                            setBarcodeSearchTerm('');
                            updateParams({barcode: null, page: null});
                        },
                    },
                ].filter(Boolean)}
            />

            {/* Products */}
            <div
                className="bg-white dark:bg-gray-800 shadow-sm overflow-hidden border border-gray-200 dark:border-gray-700">
                {products.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                        <Package className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4"/>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                            Aucun produit trouvé
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-md">
                            Ajustez la recherche ou les filtres pour retrouver vos produits.
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="hidden lg:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-50 dark:bg-gray-700/50">
                                <tr className="group">
                                    <th className="px-6 py-4 text-left">
                                        <input
                                            type="checkbox"
                                            className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            checked={selectedAll}
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <SortableHeader
                                        field="name"
                                        currentField={sortField}
                                        direction={sortDirection}
                                        onSort={handleSort}
                                    >
                                        Produit
                                    </SortableHeader>
                                    <SortableHeader
                                        field="category"
                                        currentField={sortField}
                                        direction={sortDirection}
                                        onSort={handleSort}
                                    >
                                        Catégorie
                                    </SortableHeader>
                                    <SortableHeader
                                        field="price"
                                        currentField={sortField}
                                        direction={sortDirection}
                                        onSort={handleSort}
                                    >
                                        Prix
                                    </SortableHeader>
                                    {hasIngredientProp && (
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                                        Prix d'achat
                                    </th>
                                    )}
                                    <SortableHeader
                                        field="isVendable"
                                        currentField={sortField}
                                        direction={sortDirection}
                                        onSort={handleSort}
                                    >
                                        Vendable
                                    </SortableHeader>
                                    {!hasStockableProp && (
                                    <SortableHeader
                                        field="isStockable"
                                        currentField={sortField}
                                        direction={sortDirection}
                                        onSort={handleSort}
                                    >
                                        Stockable
                                    </SortableHeader>
                                    )}
                                    <SortableHeader
                                        field="active"
                                        currentField={sortField}
                                        direction={sortDirection}
                                        onSort={handleSort}
                                    >
                                        Statut
                                    </SortableHeader>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                </tr>
                                </thead>
                                <tbody
                                    className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                {sortedProducts.map(p => (
                                    <tr
                                        key={p.id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                        onClick={() => handleEditProduct(p.id)}
                                    >
                                        <td className="px-6 py-4">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                checked={selectedItems.includes(p.id)}
                                                onChange={(e) => handleSelectItem(p.id, e)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="h-12 w-12 flex-shrink-0 flex items-center justify-center text-white text-sm font-bold rounded-lg shadow-sm"
                                                    style={{backgroundColor: p.color || '#4DB6AC'}}
                                                >
                                                    {p.imageUrl && p.imageUrl !== '' ? (
                                                        <img
                                                            src={`${p.thumbnailUrl || p.imageUrl}`}
                                                            alt={p.name}
                                                            className="h-full w-full object-cover rounded-lg"
                                                        />
                                                    ) : (
                                                        p.name.substring(0, 2).toUpperCase()
                                                    )}
                                                </div>
                                                <div>
                                                    <div
                                                        className="text-sm font-semibold text-gray-900 dark:text-gray-100">{p.name}</div>
                                                    {p.barcode && (
                                                        <div
                                                            className="flex items-center gap-1 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                                                            <Barcode className="w-3 h-3 flex-shrink-0"/>
                                                            <span className="font-mono">{p.barcode}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            {p.category ? (
                                                <span
                                                    className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium shadow-sm"
                                                    style={{
                                                        backgroundColor: `${p.category.color}20`,
                                                        color: p.category.color
                                                    }}
                                                >
                                                    {p.category.name}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 text-sm">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-semibold text-gray-900 dark:text-gray-100">
                                            {p.variants && p.variants.length > 0 ? (
                                                <span
                                                    className="px-3 py-1 bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary rounded-full text-xs font-medium">
                                                    {p.variants.length} variante{p.variants.length > 1 ? 's' : ''}
                                                </span>
                                            ) : (
                                                <span>{p.price ? `${Number(p.price).toFixed(2)} DH` : '—'}</span>
                                            )}
                                        </td>
                                        {hasIngredientProp && (
                                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                                            {p.purchasePriceHt != null ? (
                                                <span>
                                                    {Number(p.purchasePriceHt).toFixed(2)} DH HT
                                                    {p.purchasePriceTtc != null && (
                                                        <span className="text-gray-400"> / {Number(p.purchasePriceTtc).toFixed(2)} DH TTC</span>
                                                    )}
                                                </span>
                                            ) : (
                                                <span className="text-orange-500 text-xs">Non défini</span>
                                            )}
                                        </td>
                                        )}
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                                    p.isVendable
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                }`}>
                                                {p.isVendable ? 'Oui' : 'Non'}
                                            </span>
                                        </td>
                                        {!hasStockableProp && (
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                                    p.isStockable
                                                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                }`}>
                                                {p.isStockable ? 'Oui' : 'Non'}
                                            </span>
                                        </td>
                                        )}
                                        <td className="px-6 py-4">
                                            <span
                                                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                                    p.active
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                }`}>
                                                {p.active ? 'Actif' : 'Inactif'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <ProductActions
                                                product={p}
                                                onEdit={handleEditProduct}
                                                onArchive={handleArchiveProduct}
                                                onDuplicate={handleDuplicateProduct}
                                                onViewDetails={handleViewDetails}
                                                onPrintLabel={handlePrintLabel}
                                                onProductReport={handleProductReport}
                                            />
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Cards */}
                        {/* Mobile rows — edge to edge, same as the Stock / Clients lists */}
                        <div className={mobileListClass}>
                            {sortedProducts.map(p => (
                                <div
                                    key={p.id}
                                    className={`${mobileRowClass} cursor-pointer ${selectedItems.includes(p.id) ? '!bg-primary/5 dark:!bg-primary/10' : ''}`}
                                    onClick={() => handleEditProduct(p.id)}
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        <input
                                            type="checkbox"
                                            className="mt-1 w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                                            checked={selectedItems.includes(p.id)}
                                            onChange={(e) => handleSelectItem(p.id, e)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <div className="h-16 w-16 flex-shrink-0">
                                            {p.imageUrl && p.imageUrl !== '' ? (
                                                <img
                                                    src={`${p.thumbnailUrl || p.imageUrl}`}
                                                    alt={p.name}
                                                    className="h-16 w-16 rounded-lg object-cover shadow-sm"
                                                />
                                            ) : (
                                                <div
                                                    className="h-16 w-16 rounded-lg flex items-center justify-center text-white text-base font-bold shadow-sm"
                                                    style={{backgroundColor: p.color || '#4DB6AC'}}
                                                >
                                                    {p.name.substring(0, 2).toUpperCase()}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate mb-1">{p.name}</h3>
                                            {p.barcode && (
                                                <p className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 mb-2">
                                                    <Barcode className="w-3 h-3 flex-shrink-0"/>
                                                    <span className="font-mono">{p.barcode}</span>
                                                </p>
                                            )}
                                            <div className="flex flex-wrap gap-1.5">
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                    p.active
                                                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                        : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                                                }`}>
                                                    {p.active ? 'Actif' : 'Inactif'}
                                                </span>
                                                {p.category && (
                                                    <span
                                                        className="px-2 py-1 rounded-full text-xs font-medium"
                                                        style={{
                                                            backgroundColor: `${p.category.color}20`,
                                                            color: p.category.color
                                                        }}
                                                    >
                                                        {p.category.name}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <ProductActions
                                            product={p}
                                            onEdit={handleEditProduct}
                                            onArchive={handleArchiveProduct}
                                            onDuplicate={handleDuplicateProduct}
                                            onViewDetails={handleViewDetails}
                                            onPrintLabel={handlePrintLabel}
                                            onProductReport={handleProductReport}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <span
                                                className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Prix</span>
                                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                                                {p.variants && p.variants.length > 0 ? (
                                                    <span
                                                        className="px-2 py-1 bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary rounded-full text-xs">
                                                        {p.variants.length} variante{p.variants.length > 1 ? 's' : ''}
                                                    </span>
                                                ) : (
                                                    <span>{p.price ? `${Number(p.price).toFixed(2)} DH` : '—'}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <span
                                                className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Vendable</span>
                                            <div className="font-semibold">
                                                <span
                                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                        p.isVendable
                                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                    }`}>
                                                    {p.isVendable ? 'Oui' : 'Non'}
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <span
                                                className="text-gray-500 dark:text-gray-400 text-xs block mb-1">Stockable</span>
                                            <div className="font-semibold">
                                                <span
                                                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                        p.isStockable
                                                            ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                                    }`}>
                                                    {p.isStockable ? 'Oui' : 'Non'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Floating selection bar — stays at the bottom of the screen while scrolling the list */}
            {selectedItems.length > 0 && (
                <div className="sticky bottom-4 z-30 flex justify-center px-2 pointer-events-none">
                    <div
                        role="toolbar"
                        aria-label="Actions sur la sélection"
                        className="pointer-events-auto flex items-center gap-1 sm:gap-2 p-1.5 pl-2 rounded-2xl selection-bar-appear
                            bg-white/85 dark:bg-gray-800/85 backdrop-blur-2xl backdrop-saturate-150
                            border border-white/60 dark:border-white/10
                            shadow-[0_12px_40px_-8px_rgba(0,0,0,0.3),0_0_0_0.5px_rgba(0,0,0,0.08)]"
                    >
                        <Button variant="ghost" size="sm" icon={X} title="Désélectionner"
                                onClick={() => { setSelectedItems([]); setSelectedAll(false); }}/>
                        <span className="px-1 sm:pr-3 text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {selectedItems.length}<span className="hidden sm:inline"> sélectionné{selectedItems.length > 1 ? 's' : ''}</span>
                        </span>
                        <div className="w-px h-6 bg-gray-200 dark:bg-white/10"/>
                        <Button variant="tinted" size="sm" icon={Printer} compact onClick={handlePrintLabelsBulk}>
                            Étiquettes
                        </Button>
                        <ActionMenu
                            label="Modifier la sélection"
                            align="right"
                            trigger={{variant: 'secondary', iconRight: ChevronDown, children: 'Modifier'}}
                            items={[
                                {header: 'Vendable'},
                                {label: 'Marquer comme vendable', icon: ShoppingCart, onClick: () => handleBulkAction('vendable', true)},
                                {label: 'Marquer non vendable', icon: X, onClick: () => handleBulkAction('vendable', false)},
                                {divider: true},
                                {header: 'Stockable'},
                                {label: 'Marquer comme stockable', icon: Archive, onClick: () => handleBulkAction('stockable', true)},
                                {label: 'Marquer non stockable', icon: X, onClick: () => handleBulkAction('stockable', false)},
                            ]}
                        />
                        <Button variant="danger" size="sm" icon={Trash2} compact onClick={() => handleBulkAction('delete')}>
                            Supprimer
                        </Button>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {!debouncedBarcodeSearchTerm && products.length > 0 && (
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 py-4">
                    {/* Items per page selector — shown regardless of page count so it's always changeable */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Afficher:</span>
                        <AppleSelect
                            value={itemsPerPage}
                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                            className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all cursor-pointer"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </AppleSelect>
                        <span className="text-sm text-gray-600 dark:text-gray-400">par page</span>
                    </div>

                    {/* Pagination buttons */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2">
                            <Button size="sm" icon={ChevronLeft} compact onClick={handlePrevPage} disabled={currentPage === 1}>
                                Précédent
                            </Button>
                            <div className="flex gap-1">
                                {pages.slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2)).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        aria-current={page === currentPage ? 'page' : undefined}
                                        className={buttonClass({
                                            variant: page === currentPage ? 'primary' : 'ghost',
                                            size: 'sm',
                                            iconOnly: true,
                                        })}
                                    >
                                        {page}
                                    </button>
                                ))}
                            </div>
                            <Button size="sm" iconRight={ChevronRight} compact onClick={handleNextPage} disabled={currentPage === totalPages}>
                                Suivant
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Filters Modal — same shell as the other catalogue popups */}
            {showFiltersModal && (
                <Modal
                    title="Filtres"
                    subtitle="Affinez la liste des produits"
                    onClose={() => setShowFiltersModal(false)}
                    footer={<ModalFooter onCancel={() => setShowFiltersModal(false)} onConfirm={applyFilters} label="Appliquer"/>}
                >
                    <div className="space-y-4">
                        <FilterField label="Catégorie">
                            <AppleSelect
                                value={tempFilters.category}
                                onChange={(e) => setTempFilters(f => ({...f, category: e.target.value}))}
                            >
                                <option value="">Toutes les catégories</option>
                                {categories.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </AppleSelect>
                        </FilterField>

                        <FilterField label="Marque">
                            <AppleSelect
                                value={tempFilters.brand}
                                onChange={(e) => setTempFilters(f => ({...f, brand: e.target.value}))}
                            >
                                <option value="">Toutes les marques</option>
                                {brands.map(b => (
                                    <option key={b.id} value={b.id}>{b.name}</option>
                                ))}
                            </AppleSelect>
                        </FilterField>

                        <FilterField label="Produit vendable">
                            <SegmentedControl
                                value={tempFilters.isVendable}
                                onChange={(v) => setTempFilters(f => ({...f, isVendable: v}))}
                                options={YES_NO_OPTIONS}
                            />
                        </FilterField>

                        {!hasStockableProp && (
                            <FilterField label="Produit stockable">
                                <SegmentedControl
                                    value={tempFilters.isStockable}
                                    onChange={(v) => setTempFilters(f => ({...f, isStockable: v}))}
                                    options={YES_NO_OPTIONS}
                                />
                            </FilterField>
                        )}

                        <FilterField label="Déclinaisons">
                            <SegmentedControl
                                value={tempFilters.hasVariants}
                                onChange={(v) => setTempFilters(f => ({...f, hasVariants: v}))}
                                options={VARIANTS_OPTIONS}
                            />
                        </FilterField>

                        <FilterField label="Statut">
                            <SegmentedControl
                                value={tempFilters.active}
                                onChange={(v) => setTempFilters(f => ({...f, active: v}))}
                                options={STATUS_OPTIONS}
                            />
                        </FilterField>

                        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                            <SwitchRow
                                id="filter-stock-alert"
                                label="Alerte de stock"
                                description="Stock sous le seuil minimum"
                                checked={tempFilters.stockAlert === 'true'}
                                onChange={(checked) => setTempFilters(f => ({...f, stockAlert: checked ? 'true' : ''}))}
                            />
                        </div>

                        {Object.values(tempFilters).some(v => v !== '') && (
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="w-full flex items-center justify-center gap-1.5 pt-2 text-sm font-medium text-red-500 hover:text-red-600"
                            >
                                <RotateCcw className="w-4 h-4"/>
                                Réinitialiser les filtres
                            </button>
                        )}
                    </div>
                </Modal>
            )}

            {/* Confirmation Dialog */}
            {showConfirmDialog && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm modal-appear">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Confirmer l'action</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">{getConfirmationMessage()}</p>
                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                            <Button onClick={() => { setShowConfirmDialog(false); setPendingAction(null); }}>
                                Annuler
                            </Button>
                            <Button variant={pendingAction?.type === 'delete' ? 'dangerSolid' : 'primary'}
                                    icon={pendingAction?.type === 'delete' ? Trash2 : Check}
                                    onClick={confirmAction}>
                                Confirmer
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Notification */}
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={hideNotification}
            />

            {/* Product Details Modal */}
            <ProductDetailsModal
                productId={detailsProductId}
                isOpen={detailsModalOpen}
                onClose={() => {
                    setDetailsModalOpen(false);
                    setDetailsProductId(null);
                }}
                onUpdate={() => queryClient.invalidateQueries(['products'])}
            />

            {/* Label Print Modal */}
            <LabelPrintModal
                open={labelItems.length > 0}
                type="product"
                items={labelItems}
                onClose={() => setLabelItems([])}
            />
        </div>
    );
};

export default ProductList;
